import { useEffect } from 'react';
import { ResumeTemplate } from '../../../mocks/resumeTemplates';

interface PreviewModalProps {
  template: ResumeTemplate;
  isFavorited: boolean;
  onToggleFavorite: () => void;
  onClose: () => void;
  onUse: () => void;
}

export default function PreviewModal({
  template,
  isFavorited,
  onToggleFavorite,
  onClose,
  onUse,
}: PreviewModalProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex overflow-hidden animate-fade-in">
        {/* Left: Large Image */}
        <div className="w-[55%] flex-shrink-0 bg-gray-100 relative overflow-hidden">
          <div
            className="absolute top-0 left-0 right-0 h-1.5 z-10"
            style={{ backgroundColor: template.color }}
          />
          <img
            src={template.image}
            alt={template.name}
            className="w-full h-full object-cover object-top"
          />
          {/* Badges overlay */}
          <div className="absolute top-4 left-4 flex flex-col gap-2 z-10">
            {template.badge === 'Popular' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-violet-500 to-cyan-500 text-white shadow-md">
                🔥 Popular
              </span>
            )}
            {template.badge === 'New' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500 text-white shadow-md animate-pulse">
                ✨ New
              </span>
            )}
          </div>
          <div className="absolute top-4 right-4 z-10">
            <span className="px-2.5 py-1 bg-black/60 text-white text-xs font-semibold rounded-full backdrop-blur-sm">
              {template.pages}p
            </span>
          </div>
        </div>

        {/* Right: Details */}
        <div className="flex-1 flex flex-col p-7 overflow-y-auto">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer z-20"
          >
            <i className="ri-close-line text-lg"></i>
          </button>

          {/* Header */}
          <div className="mb-5 pr-8">
            <div
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-3"
              style={{ backgroundColor: template.color + '18', color: template.color }}
            >
              <i className="ri-folder-line"></i>
              {template.category}
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{template.name}</h2>
            <p className="text-sm text-gray-500 leading-relaxed">{template.description}</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <div className="text-lg font-bold text-gray-900">{template.pages}</div>
              <div className="text-xs text-gray-500 mt-0.5">Pages</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <div className="text-lg font-bold text-gray-900">ATS</div>
              <div className="text-xs text-gray-500 mt-0.5">Optimized</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <div className="text-lg font-bold text-gray-900">
                {new Date(template.dateAdded).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">Added</div>
            </div>
          </div>

          {/* Tags */}
          <div className="mb-6">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Style Tags</p>
            <div className="flex flex-wrap gap-2">
              {template.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Color accent */}
          <div className="mb-6">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Accent Color</p>
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-full border-2 border-white shadow-md"
                style={{ backgroundColor: template.color }}
              />
              <span className="text-sm font-mono text-gray-600">{template.color}</span>
            </div>
          </div>

          {/* Features */}
          <div className="mb-6">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Features</p>
            <ul className="space-y-1.5">
              {[
                'ATS-friendly formatting',
                'Fully customizable sections',
                'Professional typography',
                'Easy to edit in any tool',
              ].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-check-line text-emerald-500"></i>
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-100">
            <button
              onClick={onToggleFavorite}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                isFavorited
                  ? 'bg-amber-50 border-amber-300 text-amber-600 hover:bg-amber-100'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-amber-300 hover:text-amber-600'
              }`}
            >
              <i className={isFavorited ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
              {isFavorited ? 'Saved' : 'Save'}
            </button>
            <button
              onClick={onUse}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all cursor-pointer whitespace-nowrap hover:opacity-90"
              style={{ backgroundColor: template.color }}
            >
              <i className="ri-edit-line"></i>
              Use This Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
